import { Box } from '@mui/material';
import { getOutOfStockOptions, IMedication, MEDICATION_STATUSES } from 'API';
import { useDictionary, useIsDesktop } from 'Hooks';
import { createContext, FC, useEffect, useState, type JSX } from 'react';
import { NotAvailableMedication } from '../NotAvailableMedication';
import { OrderSummary } from '../OrderSummary';
import { SelectMedications } from '../SelectMedications';
import { sendEvent } from 'Utils/sendEvent';
import { BGNewMobile } from 'Images';
import { PrescriptionDialogStyle } from 'Components';
import { PrescriptionAndUnhandledOrdersProvider } from 'Context/PrescriptionAndUnhandledOrdersContext';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { SuccessOrderDialog } from 'Modules/EquipmentCreateOrder/SuccessOrderDialog';

interface IStep {
  id: number;
  Component: JSX.Element;
}

interface IMedicationOption {
  code: number;
  value: string;
  description: string;
}

interface IOrderRegularMedicationsContext {
  expiredMedicationOptions: IMedicationOption[];
  handleClose: () => void;
  steps: IStep[];
  activeStep: number;
  nextStep: () => void;
  prevStep: () => void;
  checkedMedications: IMedication[];
  checkAll: boolean;
  removeCheckedMedication: (medication: IMedication) => void;
  addCheckedMedication: (medication: IMedication) => void;
  checkAllMedications: (medications: IMedication[]) => void;
  unCheckAllMedications: () => void;
  outOfStockMedications: IMedication[];
  setOptionToOutOfStockMedication: (
    medication: IMedication,
    option: string,
    outOfStockOptionCode: number,
  ) => void;
  openSuccessOrderDialog: () => void;
  setSuccessOrderDialogExpiredPrescriptions: React.Dispatch<
    React.SetStateAction<string[] | undefined>
  >;
  period: number;
  setPeriod: (period: number) => void;
  isSplit: boolean;
  setIsSplit: (isSplit: boolean) => void;
  setNumberOfOrderedItems: (num: number) => void;
}

export const OrderRegularMedicationsContext =
  createContext<IOrderRegularMedicationsContext>({
    expiredMedicationOptions: [],
    handleClose: () => {},
    steps: [],
    activeStep: 0,
    nextStep: () => {},
    prevStep: () => {},
    checkedMedications: [],
    checkAll: false,
    removeCheckedMedication: () => {},
    addCheckedMedication: () => {},
    checkAllMedications: () => {},
    unCheckAllMedications: () => {},
    outOfStockMedications: [],
    setOptionToOutOfStockMedication: () => {},
    openSuccessOrderDialog: () => {},
    setSuccessOrderDialogExpiredPrescriptions: () => {},
    period: 1,
    setPeriod: () => {},
    isSplit: true,
    setIsSplit: () => {},
    setNumberOfOrderedItems: () => {},
  });

interface IOrderRegularMedicationsProvider {
  handleClose: () => void;
  open: boolean;
  openSuccessOrderDialog: () => void;
  isCreateOrder?: boolean;

  setSuccessOrderDialogExpiredPrescriptions: React.Dispatch<
    React.SetStateAction<string[] | undefined>
  >;
}

export const OrderRegularMedicationsProvider: FC<
  IOrderRegularMedicationsProvider
> = (props) => {
  const initSteps = [
    { id: 0, Component: <SelectMedications /> },
    { id: -1, Component: <OrderSummary closeModal={props.handleClose} /> },
  ];
  const [steps, setSteps] = useState<IStep[]>(initSteps);

  const [checkAll, setCheckAll] = useState<boolean>(false);
  const [period, setPeriod] = useState<number>(1);
  const [isSplit, setIsSplit] = useState<boolean>(true);

  const [activeStep, setActiveStep] = useState<number>(0);

  const [checkedMedications, setCheckedMedications] = useState<IMedication[]>(
    [],
  );
  const [openSuccessOrderDialog, setOpenSuccessOrderDialog] = useState(false);

  const [outOfStockMedications, setOutOfStockMedications] = useState<
    IMedication[]
  >([]);

  const [expiredMedicationOptions, setExpiredMedications] = useState<
    IMedicationOption[]
  >([]);
  const [numberOfOrderedItems, setNumberOfOrderedItems] = useState(0);

  const openSuccessOrderDialogHandler = () => {
    setOpenSuccessOrderDialog(true);
  };

  const initExpiredMedicationOptions = async () => {
    const response = await getOutOfStockOptions();
    if (response) {
      setExpiredMedications(response);
    }
  };
  const dictionary = useDictionary(
    Dictionary,
    DICTIONARY.MedicationsAndPrescriptionsLobby,
  );
  useEffect(() => {
    initExpiredMedicationOptions();
  }, []);

  const isDesktop = useIsDesktop();

  const addStep = (medication: IMedication) => {
    setSteps((prevSteps) => {
      const medicationCode = medication.medicatioN_CODE || 0;
      const clonePrevSteps = [...prevSteps];
      clonePrevSteps.splice(clonePrevSteps.length - 1, 0, {
        id: medicationCode,
        Component: (
          <NotAvailableMedication outOfStockMedicationCode={medicationCode} />
        ),
      });
      return clonePrevSteps;
    });
  };

  const removeStep = (medication: IMedication) => {
    setSteps((prevSteps) => {
      return prevSteps.filter(
        (prevStep) => prevStep.id !== medication.medicatioN_CODE,
      );
    });
  };

  const setOptionToOutOfStockMedication = (
    medication: IMedication,
    option: string,
    outOfStockOptionCode: number,
  ) => {
    setOutOfStockMedications((prevOutOfStockMedications) => {
      const clonePrevOutOfStockMedications = [...prevOutOfStockMedications];
      const outOfStockMedicationIndex =
        clonePrevOutOfStockMedications.findIndex(
          (prevCloneOutOfStockMedication) =>
            prevCloneOutOfStockMedication.medicatioN_CODE ===
            medication.medicatioN_CODE,
        );
      if (outOfStockMedicationIndex !== -1) {
        clonePrevOutOfStockMedications[outOfStockMedicationIndex] = {
          ...medication,
          lackMedicineNote: option,
          OutOfStockOptionCode: outOfStockOptionCode,
        };
      }
      return clonePrevOutOfStockMedications;
    });
  };
  const handleClose = () => {
    sendEvent('Medicine', 'CloseButtonNewOrder');
    setCheckAll(false);
    setActiveStep(0);
    setCheckedMedications([]);
    setSteps(initSteps);
    setOutOfStockMedications([]);
    props.handleClose();
  };

  const addOutOfStockMedications = (medication: IMedication) => {
    if (medication.medstatus !== MEDICATION_STATUSES.notAvailable) return;
    addStep(medication);
    setOutOfStockMedications((prevOutOfStockMedications) => {
      const clonePrevOutOfStockMedications = [...prevOutOfStockMedications];
      medication.lackMedicineNote = '';
      clonePrevOutOfStockMedications.push(medication);
      return clonePrevOutOfStockMedications;
    });
  };

  const removeOutOfStockMedications = (medication: IMedication) => {
    if (medication.medstatus !== MEDICATION_STATUSES.notAvailable) return;
    removeStep(medication);
    setOutOfStockMedications((prevOutOfStockMedications) => {
      return prevOutOfStockMedications.filter(
        (prevOutOfStockMedication) =>
          prevOutOfStockMedication.medicatioN_CODE !==
          medication.medicatioN_CODE,
      );
    });
  };

  const nextStep = () => {
    sendEvent('Medicine', 'ContinueButtonMedicineOrder');
    const titleEl = document.getElementById('notAvailableMedicineTitle');
    titleEl?.focus();
    setActiveStep(activeStep + 1);
  };

  const prevStep = () => {
    const titleEl = document.getElementById('notAvailableMedicineTitle');
    titleEl?.focus();
    setActiveStep(activeStep - 1);
  };

  const checkAllMedications = (medications: IMedication[]) => {
    setCheckAll(true);
    setCheckedMedications([]);
    setOutOfStockMedications([]);
    setSteps(initSteps);
    medications.forEach((medication) => {
      addCheckedMedication(medication);
    });
  };

  const unCheckAllMedications = () => {
    setCheckAll(false);
    setCheckedMedications([]);
    setOutOfStockMedications([]);
    setSteps(initSteps);
  };

  const addCheckedMedication = (medication: IMedication) => {
    if (medication.medstatus === MEDICATION_STATUSES.notAvailable) {
      addOutOfStockMedications(medication);
    } else {
      setCheckedMedications((prevCheckedMedication) => {
        const clonePrevCheckedMedication = [...prevCheckedMedication];
        clonePrevCheckedMedication.push(medication);
        return clonePrevCheckedMedication;
      });
    }
  };

  const removeCheckedMedication = (medication: IMedication) => {
    if (checkAll) {
      setCheckAll(false);
    }
    if (medication.medstatus === MEDICATION_STATUSES.notAvailable) {
      removeOutOfStockMedications(medication);
    } else {
      setCheckedMedications((prevCheckedMedication) => {
        return prevCheckedMedication.filter(
          (prevOutOfStockMedication) =>
            prevOutOfStockMedication.medicatioN_CODE !==
            medication.medicatioN_CODE,
        );
      });
    }
  };

  const getStepComponent = () => {
    if (steps[activeStep]) {
      return steps[activeStep].Component;
    }
    return null;
  };
  const dialogTitleStyle = {
    padding: isDesktop ? '0' : '35px 24px 16px 24px',
    background: isDesktop ? '#fff' : `url(${BGNewMobile}) no-repeat`,
    backgroundSize: isDesktop ? undefined : '100%',
    paddingRight: '24px',
    paddingLeft: '24px',
    height: isDesktop ? '92px' : '135px',
  };

  return (
    <PrescriptionAndUnhandledOrdersProvider>
      <OrderRegularMedicationsContext.Provider
        value={{
          expiredMedicationOptions,
          handleClose,
          steps,
          activeStep,
          nextStep,
          prevStep,
          checkedMedications,
          checkAll,
          period,
          setPeriod,
          isSplit,
          setIsSplit,
          removeCheckedMedication,
          addCheckedMedication,
          checkAllMedications,
          unCheckAllMedications,
          outOfStockMedications,
          setOptionToOutOfStockMedication,
          openSuccessOrderDialog: openSuccessOrderDialogHandler,
          setSuccessOrderDialogExpiredPrescriptions:
            props.setSuccessOrderDialogExpiredPrescriptions,
          setNumberOfOrderedItems,
        }}
      >
        {props.isCreateOrder && <SelectMedications />}

        <PrescriptionDialogStyle
          open={props.open && activeStep !== 0}
          onClose={handleClose}
          labelledby='summary-order-dialog-title'
          describedby='summary-order-dialog-content'
        >
          <Box
            sx={{
              background: dialogTitleStyle.background,
              backgroundSize: dialogTitleStyle.backgroundSize,
            }}
          >
            {getStepComponent()}
          </Box>
        </PrescriptionDialogStyle>

        <SuccessOrderDialog
          handleClose={handleClose}
          numberOfItems={numberOfOrderedItems}
          open={openSuccessOrderDialog}
          link={'/medicine'}
          linkLabel={dictionary.medicineLineLabel}
        />
      </OrderRegularMedicationsContext.Provider>
    </PrescriptionAndUnhandledOrdersProvider>
  );
};
